'use client';

import { RefObject, useEffect, useState } from 'react';

type Condition = {
  minmax: 'min' | 'max';
  dimension: 'width' | 'height';
  value: number;
  unit: string;
};

function useContainerQuery(ref: RefObject<HTMLElement | null>, query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const element = ref.current;
    if (!element) return;

    const parsedQuery = parseQuery(query);

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;

        const doesMatch = evaluateParsedQuery(parsedQuery, width, height);

        setMatches(doesMatch);
      }
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [ref, query]);

  return matches;
}

function parseQuery(query: string): Condition[] {
  // Simplified parser for media query strings
  // Only handles (min-width: Xpx), (max-width: Xpx), (min-height: Xpx), (max-height: Xpx)

  const conditions = query.match(/\(([^)]+)\)/g); // Match all conditions inside parentheses
  if (!conditions) return [];

  const parsedConditions = conditions.map((condition) => {
    const [feature, valueWithUnit] = condition
      .replace('(', '')
      .replace(')', '')
      .split(':')
      .map((s) => s.trim());

    const match = feature.match(/(min|max)-(width|height)/);
    if (!match) {
      console.warn(`Unsupported feature: ${feature}`);
      return null;
    }

    const [, minmax, dimension] = match;
    const number = parseFloat(valueWithUnit);
    const unit = valueWithUnit.replace(String(number), '').trim();

    return {
      minmax: minmax as 'min' | 'max',
      dimension: dimension as 'width' | 'height',
      value: number,
      unit,
    } as Condition;
  });

  return parsedConditions.filter((c) => c != null) as Condition[];
}

function evaluateParsedQuery(conditions: Condition[], width: number, height: number): boolean {
  return conditions.every((condition) => {
    const { minmax, dimension, value, unit } = condition;
    let actualValue = dimension === 'width' ? width : height;

    // Convert unit if necessary, for simplicity assume px
    if (unit !== 'px') {
      console.warn(`Unsupported unit: ${unit}, only 'px' is supported.`);
      return false;
    }

    if (minmax === 'min') {
      return actualValue >= value;
    } else if (minmax === 'max') {
      return actualValue <= value;
    } else {
      return false;
    }
  });
}

export { useContainerQuery };
